import { Button, Tooltip } from "antd";
import { useMemo } from "react";
import { VizIcon } from "src/components/VizIcon";
import { Flex } from "src/components/Flex";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { DeepLinkType, toExploreV1State } from "pages/api/deep-links/types";
import { ExploreContext } from "./useExploreContext";
import { BarChartControls } from "./lib/Chart";
import { HeatMapControls } from "./lib/HeatMap";
import { TreeMapControls } from "./lib/TreeMap";
import { OverTimeControls } from "./lib/OverTime";
import { MeasureTableControls } from "./lib/MeasureTable";
import { EntitiesTableControls } from "./lib/EntitiesTable";
import { SavedVisualizations } from "./SavedVisualizations";
import { ContentViews } from "./lib/useContentView";
import { MetricControls } from "./lib/Metric";
import { FullscreenButton } from "./FullscreenButton";
import { PivotTableV2Controls } from "./lib/PivotTableV2";

export function ResponsePanels({
  context,
  enableFullScreenToggling,
  collapsed,
}: {
  context: ExploreContext;
  enableFullScreenToggling?: boolean;
  collapsed?: boolean;
}) {
  const { view, params, doc } = context;
  const groupBys = params.state.groupBys || [];

  const controls = useMemo(() => {
    // Handle unified table view first
    if (
      view.value === ContentViews.unifiedTable ||
      view.value === ContentViews.nestedTable
    ) {
      // Determine which table controls to show based on the current layout and state
      const layout = view.config?.layout || "flat";
      const hasGroupBys = params.selectors.hasGroupBys(params.state);

      // If there are no groups, always show entities table controls
      if (!hasGroupBys) {
        return <EntitiesTableControls context={context} />;
      }

      // Only show specialized controls if we have groups
      switch (layout) {
        case "pivot": {
          return <PivotTableV2Controls context={context} />;
        }
        case "nested": {
          return (
            <PivotTableV2Controls
              context={context}
              controlsConfig={{
                showGroupSelector: false,
                showPaging: true,
                showWrapping: true,
                showCompare: true,
                showLayoutSelector: true,
                showRollupMeasureSelector: false,
              }}
            />
          );
        }
        default: {
          return <MeasureTableControls context={context} />;
        }
      }
    }

    // Handle other views
    return view.value === ContentViews.barChart ? (
      <BarChartControls context={context} />
    ) : view.value === ContentViews.heatMap ? (
      <HeatMapControls context={context} />
    ) : view.value === ContentViews.treeMap ? (
      <TreeMapControls context={context} />
    ) : view.value === ContentViews.overTime ? (
      <OverTimeControls context={context} />
    ) : view.value === ContentViews.metric ? (
      <MetricControls context={context} />
    ) : view.value === ContentViews.pivotTable && groupBys.length > 0 ? (
      <PivotTableV2Controls context={context} />
    ) : view.value === ContentViews.dataTable ? (
      params.selectors.hasGroupBys(params.state) ? (
        <MeasureTableControls context={context} />
      ) : (
        <EntitiesTableControls context={context} />
      )
    ) : null;
  }, [view.value, view.config.layout, context, groupBys.length, params]);

  const links = useDeepLinks(DeepLinkType.EXPLORE);
  const link = links.links.data?.find((link) => link.id === doc.saved);
  const linkState = useMemo(
    () => toExploreV1State(link?.state || ""),
    [link?.state]
  );
  return (
    <div>
      {enableFullScreenToggling && (
        <div
          style={{
            padding: collapsed ? "6px 2px" : undefined,
            width: "100%",
          }}
        >
          <Tooltip
            mouseEnterDelay={0}
            open={collapsed ? undefined : false}
            title="Show Settings"
            placement="left"
          >
            <div
              style={{
                textAlign: collapsed ? "center" : "left",
                width: "100%",
              }}
            >
              <FullscreenButton context={context} />
            </div>
          </Tooltip>
        </div>
      )}
      {collapsed ? (
        <Flex style={{ marginTop: 4, width: "100%" }}>
          <Tooltip title="Change View" placement="left" mouseEnterDelay={0}>
            <Button
              size="large"
              style={{
                width: "100%",
                padding: "9px 0",
                display: "flex",
                justifyContent: "center",
                margin: "4px",
              }}
              icon={<VizIcon value={view.value} />}
              onClick={() => {
                context.tabs.setFullscreen((v) => !v);
              }}
            />
          </Tooltip>
        </Flex>
      ) : (
        <div className="section">
          <div className="section-header">Visualization Options</div>
          <SavedVisualizations context={context} vizs={linkState.viz} />
          {controls && <div className="viz-controls">{controls}</div>}
        </div>
      )}
    </div>
  );
}
